import { CustomerBalanceData, Invoice, InvoiceStatus, LineItem, StatementPaymentSummary } from './invoice';
import { DateTime } from 'luxon';
import { User } from '../user/user';
import { PaymentParsers } from '../payment/payment-parsers';
import { TaxCodeParsers } from '../tax/tax-parsers';

export namespace InvoiceParsers {
  export type InvoiceJson = {
    id: string;
    customer: User;
    locationId: number;
    invoiceNumber?: string;
    status: InvoiceStatus;
    type: 'Order' | 'WorkOrder' | 'PurchaseOrder';
    documentType: 'Invoice' | 'CreditMemo' | 'PurchaseOrder';
    typeId: string;
    taxRate: number;
    taxCode?: TaxCodeParsers.TaxCodeJson;
    taxAdjustment?: number;
    subtotalAdjustment?: number;
    totalAdjustment?: number;
    lineItems: LineItemJson[];
    payments: PaymentParsers.PaymentJson[];
    subtotal: number;
    tax: number;
    total: number;
    balance: number;
    date?: string;
    dueDate?: string;
    locked: boolean;
    cycleNumber?: number;
    createdAt: string;
    updatedAt: string;
  };

  export type LineItemJson = {
    id: string;
    invoiceId: string;
    type:
      | 'OrderProduct'
      | 'ProductFee'
      | 'OrderFee'
      | 'Coupon'
      | 'DamageWaiver'
      | 'Delivery'
      | 'OrderPayment'
      | 'Tax'
      | 'CreditCardFee'
      | 'AdditionalDiscount';
    typeId: string;
    name: string;
    description?: string;
    unitPrice: number;
    quantity: number;
    total: number;
    taxable: boolean;
    taxOverride?: number;
    generalLedgerAccountId: string;
    taxCategoryId?: string;
    createdAt: string;
    createdBy?: number;
    updatedAt: string;
    updatedBy?: number;
    meta?: Record<string, any>;
  };

  export type CustomerBalanceDataJson = {
    items: CustomerBalanceJson[];
  };

  export type CustomerBalanceJson = {
    customerId: number;
    pastDue: number;
    daysPastDue: number;
    due: number;
    totalDue: number;
    lifetimeTotal: number;
  };

  export type StatementPaymentSummaryJson = { invoiceNumber?: string; invoiceId: string; amount: number; due: number };

  export function toInvoice(input: InvoiceJson): Invoice {
    return {
      id: input.id,
      customer: input.customer,
      locationId: input.locationId,
      invoiceNumber: input.invoiceNumber,
      status: input.status,
      type: input.type,
      typeId: input.typeId,
      documentType: input.documentType,
      taxRate: input.taxRate,
      taxCode: input.taxCode && TaxCodeParsers.toTaxCode(input.taxCode),
      taxAdjustment: input.taxAdjustment,
      subtotalAdjustment: input.subtotalAdjustment,
      totalAdjustment: input.totalAdjustment,
      lineItems: input.lineItems.map(toLineItem),
      payments: input.payments.map(PaymentParsers.toPayment),
      subtotal: input.subtotal,
      tax: input.tax,
      total: input.total,
      balance: input.balance,
      date: input.date ? DateTime.fromISO(input.date, { setZone: true }) : undefined,
      dueDate: input.dueDate ? DateTime.fromISO(input.dueDate, { setZone: true }) : undefined,
      locked: input.locked,
      cycleNumber: input.cycleNumber,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      updatedAt: DateTime.fromISO(input.updatedAt, { setZone: true }),
    };
  }

  export function toLineItem(input: LineItemJson): LineItem {
    return {
      id: input.id,
      invoiceId: input.invoiceId,
      typeId: input.typeId,
      type: input.type,
      name: input.name,
      description: input.description,
      unitPrice: input.unitPrice,
      quantity: input.quantity,
      total: input.total,
      taxable: input.taxable,
      taxOverride: input.taxOverride,
      generalLedgerAccountId: input.generalLedgerAccountId,
      taxCategoryId: input.taxCategoryId,
      createdAt: DateTime.fromISO(input.createdAt, { setZone: true }),
      createdBy: input.createdBy,
      updatedAt: DateTime.fromISO(input.updatedAt, { setZone: true }),
      updatedBy: input.updatedBy,
      meta: input.meta,
    };
  }

  export function toCustomerBalance(input: CustomerBalanceJson): CustomerBalanceData {
    return {
      customerId: input.customerId,
      pastDue: input.pastDue,
      daysPastDue: input.daysPastDue,
      due: input.due,
      totalDue: input.totalDue,
      lifetimeTotal: input.lifetimeTotal,
    };
  }

  export function toStatementPaymentSummary(input: StatementPaymentSummaryJson): StatementPaymentSummary {
    return {
      invoiceNumber: input.invoiceNumber,
      invoiceId: input.invoiceId,
      amount: input.amount,
      due: input.due,
    };
  }
}
